import { defineMutator } from '@rocicorp/zero';
import { createPatch } from 'rfc6902';
import { uuidv7 } from 'uuidv7';
import * as z from 'zod';
import { zql } from '../schema.js';
import { itemsDetail, statusItems } from '../util/event-log-items.js';
import { assertCan } from '../util/permissions.js';
import { Address, Status } from '../util/schemas.js';
import { UpdateResourceStatusSchema, addEventLog } from './util.js';
import { createAddress, updateAddress } from './utils/address-utils.js';
export const ProductionLocationInput = z.object({
    id: z.uuidv7().optional(),
    name: z.string(),
    status: Status.optional(),
});
export const PharmacySchema = z.object({
    name: z.string(),
    code: z.string().trim().nonempty(),
    email: z.string().optional(),
    phone: z.string().optional(),
    address: Address,
    deliveryAddress: Address.optional(),
    isSameAddress: z.boolean().optional(),
    status: Status.optional(),
    ikNumber: z.string().trim().nonempty(),
    producerNumber: z.string().optional(),
    labelName: z.string().optional(),
    productionLocations: z.array(ProductionLocationInput).default([]),
});
export const AddPharmacySchema = PharmacySchema;
export const UpdatePharmacySchema = AddPharmacySchema.partial().extend({ id: z.uuidv7() });
export const pharmacies = {
    add: defineMutator(AddPharmacySchema, async ({ args: pharmacy, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        if (pharmacy.code) {
            const existingCode = await tx.run(zql.pharmacies.where('code', '=', pharmacy.code).one());
            if (existingCode)
                throw new Error('Pharmacy code already exists');
        }
        if (pharmacy.ikNumber) {
            const existingIk = await tx.run(zql.pharmacies.where('ikNumber', '=', pharmacy.ikNumber).one());
            if (existingIk)
                throw new Error('Pharmacy IK number already exists');
        }
        const location = await tx.run(zql.locations.one());
        if (!location)
            throw new Error('Could not find location');
        const locationId = location.id;
        const address = await createAddress(tx, pharmacy.address, ctx.user.id);
        let deliveryAddressId = address.id;
        if (pharmacy.deliveryAddress && !pharmacy.isSameAddress) {
            const deliveryAddress = await createAddress(tx, pharmacy.deliveryAddress, ctx.user.id);
            deliveryAddressId = deliveryAddress.id;
        }
        const now = Date.now();
        const newPharmacy = {
            id: uuidv7(),
            name: pharmacy.name,
            code: pharmacy.code,
            locationId: locationId,
            addressId: address?.id,
            isSameAddress: pharmacy.isSameAddress ?? true,
            deliveryAddressId: pharmacy.isSameAddress === false ? deliveryAddressId : address?.id,
            ikNumber: pharmacy.ikNumber,
            producerNumber: pharmacy.producerNumber,
            email: pharmacy.email,
            phone: pharmacy.phone,
            status: pharmacy.status ?? 'active',
            createdAt: now,
            createdBy: ctx.user.id,
        };
        await tx.mutate.pharmacies.insert(newPharmacy);
        const newProductionLocations = (pharmacy.productionLocations ?? []).map(pl => ({
            id: uuidv7(),
            name: pl.name,
            status: pl.status ?? 'active',
            pharmacyId: newPharmacy.id,
            createdAt: now,
            createdBy: ctx.user.id,
        }));
        await Promise.all(newProductionLocations.map(pl => tx.mutate.productionLocations.insert(pl)));
        if (tx.location === 'server') {
            const patches = createPatch({}, newPharmacy);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'pharmacy',
                resourceId: newPharmacy.id,
                action: 'create',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: newPharmacy.id,
                    name: newPharmacy.name,
                },
            }, ctx);
        }
    }),
    update: defineMutator(UpdatePharmacySchema, async ({ args: pharmacy, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const prev = await tx.run(zql.pharmacies
            .where('id', '=', pharmacy.id)
            .related('productionLocations', q => q.where('deletedAt', 'IS', null))
            .one());
        if (!prev)
            throw new Error('Not Found');
        if (pharmacy.code) {
            const existingCode = await tx.run(zql.pharmacies.where('code', '=', pharmacy.code).where('id', '!=', pharmacy.id).one());
            if (existingCode)
                throw new Error('Pharmacy code already exists');
        }
        if (pharmacy.ikNumber) {
            const existingIk = await tx.run(zql.pharmacies.where('ikNumber', '=', pharmacy.ikNumber).where('id', '!=', pharmacy.id).one());
            if (existingIk)
                throw new Error('Pharmacy IK number already exists');
        }
        const now = Date.now();
        const incoming = pharmacy.productionLocations ?? [];
        const incomingIds = new Set(incoming.filter(pl => !!pl.id).map(pl => pl.id));
        const productionLocationsToDelete = prev.productionLocations?.filter(pl => !incomingIds.has(pl.id)) ?? [];
        // Soft delete missing production locations
        await Promise.all(productionLocationsToDelete.map(pl => tx.mutate.productionLocations.update({
            id: pl.id,
            deletedAt: now,
            deletedBy: ctx.user.id,
        })));
        const upserts = incoming.map(pl => ({
            id: pl.id || uuidv7(),
            name: pl.name,
            status: pl.status ?? 'active',
            pharmacyId: prev.id,
            createdAt: now,
            createdBy: ctx.user.id,
        }));
        await Promise.all(upserts.map(pl => tx.mutate.productionLocations.upsert(pl)));
        if (prev.addressId && pharmacy.address) {
            await updateAddress(tx, prev.addressId, pharmacy.address, ctx.user.id);
        }
        let deliveryAddressId = prev.deliveryAddressId;
        if (prev.isSameAddress !== pharmacy.isSameAddress) {
            if (pharmacy.isSameAddress) {
                deliveryAddressId = prev.addressId;
            }
            else if (pharmacy.deliveryAddress) {
                const newDeliveryAddress = await createAddress(tx, pharmacy.deliveryAddress, ctx.user.id);
                deliveryAddressId = newDeliveryAddress.id;
            }
        }
        delete pharmacy.address;
        delete pharmacy.deliveryAddress;
        const updatedPharmacy = {
            ...pharmacy,
            id: prev.id,
            updatedAt: now,
            updatedBy: ctx.user.id,
            deliveryAddressId,
            productionLocations: undefined,
        };
        await tx.mutate.pharmacies.update(updatedPharmacy);
        if (tx.location === 'server') {
            const patches = createPatch(prev, { ...prev, ...pharmacy, updatedAt: now, updatedBy: ctx.user.id });
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'pharmacy',
                resourceId: prev.id,
                action: 'update',
                patches,
                performedBy: ctx.user.id,
                performedAt: now,
                status: 'OK',
                domain: 'configuration',
                details: {
                    id: updatedPharmacy.id,
                    name: updatedPharmacy.name,
                },
            }, ctx);
        }
    }),
    updateStatus: defineMutator(UpdateResourceStatusSchema, async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const prev = await tx.run(zql.pharmacies
            .where('id', '=', args.id)
            .related('productionLocations', q => q.where('deletedAt', 'IS', null))
            .one());
        if (!prev)
            throw new Error('Not found');
        const updatedPharmacy = {
            id: args.id,
            status: args.status,
            updatedAt: Date.now(),
            updatedBy: ctx.user.id,
        };
        await tx.mutate.pharmacies.update(updatedPharmacy);
        if (tx.location === 'server') {
            const patches = createPatch(prev, updatedPharmacy);
            await addEventLog(tx, {
                id: uuidv7(),
                resource: 'pharmacy',
                resourceId: args.id,
                action: 'update',
                patches: patches,
                performedBy: updatedPharmacy.updatedBy,
                performedAt: updatedPharmacy.updatedAt,
                status: args.status === 'inactive' ? 'WARNING' : 'INFO',
                domain: 'configuration',
                details: {
                    id: updatedPharmacy.id,
                    name: prev.name,
                    ...itemsDetail(statusItems(prev.status, args.status)),
                },
            }, ctx);
        }
    }),
    deactivateBatch: defineMutator(z.object({ pharmacyIds: z.array(z.uuidv7()) }), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const rows = await tx.run(zql.pharmacies.where('id', 'IN', args.pharmacyIds).where('status', '=', 'active'));
        const now = Date.now();
        await Promise.all(rows.map(pharmacy => tx.mutate.pharmacies.update({
            id: pharmacy.id,
            status: 'inactive',
            updatedAt: now,
            updatedBy: ctx.user.id,
        })));
        if (tx.location === 'server') {
            await Promise.all(rows.map(pharmacy => {
                const patches = createPatch(pharmacy, {
                    ...pharmacy,
                    status: 'inactive',
                    updatedAt: now,
                    updatedBy: ctx.user.id,
                });
                return addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'pharmacy',
                    resourceId: pharmacy.id,
                    action: 'update',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        id: pharmacy.id,
                        name: pharmacy.name,
                    },
                }, ctx);
            }));
        }
    }),
    deleteBatch: defineMutator(z.object({ pharmacyIds: z.array(z.uuidv7()) }), async ({ args, ctx, tx }) => {
        assertCan(ctx, 'organisation.manage');
        const rows = await tx.run(zql.pharmacies.where('id', 'IN', args.pharmacyIds));
        const now = Date.now();
        await Promise.all(rows.map(pharmacy => tx.mutate.pharmacies.update({
            id: pharmacy.id,
            deletedAt: now,
            deletedBy: ctx.user.id,
            code: `${pharmacy.code}_deleted_${now}`,
            ikNumber: `${pharmacy.ikNumber}_deleted_${now}`,
        })));
        if (tx.location === 'server') {
            await Promise.all(rows.map(pharmacy => {
                const patches = createPatch(pharmacy, {
                    ...pharmacy,
                    deletedAt: now,
                    deletedBy: ctx.user.id,
                    code: `${pharmacy.code}_deleted_${now}`,
                    ikNumber: `${pharmacy.ikNumber}_deleted_${now}`,
                });
                return addEventLog(tx, {
                    id: uuidv7(),
                    resource: 'pharmacy',
                    resourceId: pharmacy.id,
                    action: 'delete',
                    patches,
                    performedBy: ctx.user.id,
                    performedAt: now,
                    status: 'WARNING',
                    domain: 'configuration',
                    details: {
                        id: pharmacy.id,
                        name: pharmacy.name,
                    },
                }, ctx);
            }));
        }
    }),
};
//# sourceMappingURL=pharmacy.mutators.js.map